import { lazy, Suspense, useCallback, useRef } from "react";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { track } from "../../../lib/analytics";
import type { useDemoData } from "../hooks/useDemoData";

const DemoCalendar = lazy(() => import("./demo/DemoCalendar").then((m) => ({ default: m.DemoCalendar })));
const DemoTaskList = lazy(() => import("./demo/DemoTaskList").then((m) => ({ default: m.DemoTaskList })));
const DemoHabits = lazy(() => import("./demo/DemoHabits").then((m) => ({ default: m.DemoHabits })));
const DemoTerminal = lazy(() => import("./demo/DemoTerminal").then((m) => ({ default: m.DemoTerminal })));

function DemoCard({
  label,
  description,
  sectionName,
  className,
  onFirstInteraction,
  children,
}: {
  readonly label: string;
  readonly description?: string;
  readonly sectionName: string;
  readonly className?: string;
  readonly onFirstInteraction?: () => void;
  readonly children: React.ReactNode;
}) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.15, sectionName);
  const interactedRef = useRef(false);

  const handlePointerDown = onFirstInteraction
    ? () => {
        if (interactedRef.current) return;
        interactedRef.current = true;
        onFirstInteraction();
      }
    : undefined;

  return (
    <div
      ref={ref}
      onPointerDown={handlePointerDown}
      className={`rounded-2xl border border-border bg-surface-alt p-5 transition-all duration-700 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      } ${className ?? ""}`}
    >
      <div className="mb-4">
        <h3 className="text-lg font-bold text-ink">{label}</h3>
        {description && (
          <p className="mt-1 text-sm text-ink-muted">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

interface FeaturesSectionProps {
  readonly demo: ReturnType<typeof useDemoData>;
}

function useDemoInteraction() {
  const firedRef = useRef(new Set<string>());
  return useCallback((demo: string) => {
    if (firedRef.current.has(demo)) return;
    firedRef.current.add(demo);
    track("landing_demo_interacted", { demo });
  }, []);
}

export function FeaturesSection({ demo }: FeaturesSectionProps) {
  const trackInteraction = useDemoInteraction();

  return (
    <section className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-extrabold text-ink">
            Essayez avant de vous inscrire.
          </h2>
          <p className="mt-4 text-ink-muted leading-relaxed max-w-xl mx-auto">
            Pas de promesses en l'air — tout ce que vous voyez en dessous est
            interactif. Testez directement une partie des fonctionnalités.
          </p>
          <p className="mt-2 text-xs text-ink-faint">
            Démos optimisées desktop — version mobile en cours de développement.
          </p>
        </div>

        <Suspense fallback={<div className="h-40 flex items-center justify-center text-sm text-ink-muted">Chargement des démos…</div>}>
        <div className="space-y-6">
          <DemoCard
            label="Calendrier"
            description="Voyez où va vraiment votre temps. Événements récurrents, vue semaine, créneaux libres."
            sectionName="demo_calendar"
            onFirstInteraction={() => trackInteraction("calendar")}
          >
            <div className="h-[500px] rounded-xl overflow-hidden">
              <DemoCalendar
                events={demo.events}
                tasks={demo.tasks}
                onAddEvent={demo.addEvent}
                onDeleteEvent={demo.deleteEvent}
                onUpdateEvent={demo.updateEvent}
              />
            </div>
          </DemoCard>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DemoCard
              label="Tâches"
              description="Priorités, sous-tâches, catégories — créez, organisez, c'est fait."
              sectionName="demo_tasks"
              onFirstInteraction={() => trackInteraction("tasks")}
            >
              <DemoTaskList
                tasks={demo.tasks}
                onAdd={demo.addTask}
                onCycleStatus={demo.cycleStatus}
                onUpdate={demo.updateTask}
                onDelete={demo.deleteTask}
                onAddSubtask={demo.addSubtask}
                onToggleSubtask={demo.toggleSubtask}
                onDeleteSubtask={demo.deleteSubtask}
              />
            </DemoCard>

            <DemoCard
              label="Habitudes"
              description="Un suivi clair de ce que vous faites vraiment, jour après jour."
              sectionName="demo_habits"
              onFirstInteraction={() => trackInteraction("habits")}
            >
              <DemoHabits />
            </DemoCard>
          </div>

          <DemoCard
            label="CLI"
            description="Gérez Palnia depuis votre terminal. Tâches, événements — tout en une commande."
            sectionName="demo_cli"
            onFirstInteraction={() => trackInteraction("cli")}
          >
            <DemoTerminal
              onAddTask={demo.addTask}
              onAddEvent={demo.addEvent}
            />
          </DemoCard>
        </div>
        </Suspense>
      </div>
    </section>
  );
}
